import { defineComponent, watch, computed, provide, defineAsyncComponent, onBeforeUnmount, ref } from 'vue';
import { useStore } from 'vuex';
import { useIdle } from '@vueuse/core';
import { MainTemplate, WithdrawalPopup } from '@hems/component';
import { AnnualReportPopup } from '@hems/container';
import { UserInfoService, DeviceServiceUser, CommonService, NotificationService, HomeApplianceService, StormCoverService, } from '@hems/service';
import { AuthHelper, Helper, Constant, DeviceHelper, useLocale, useMessageBox, useAccessToken } from '@hems/util';
import { AC_DEVICE_TYPE, AC_MODEL_TYPE, CONSUMPTION_CT_TYPE, EMS_TYPE, GEN_TYPE, ONE_HOUR_VALUE_IN_MILLISECONDS, PROVIDE_INJECT_KEYS, } from '@hems/util/src/constant';
import { Header, Footer, PrivacyPopup } from '@/components';
import HomeInfoPopup from '@/containers/popup/homeinfo/HomeInfoPopup.vue';
import SevereWeatherPopup from '@/containers/popup/SevereWeatherPopup.vue';
import { DEVICE_ACTION, STORM_ACTION } from '@/stores';
import { SITE_ACTION } from '@/stores/modules/site';
const POPUP_NAME = {
    PRIVACY: 'privacy',
    HOME_INFO: 'homeInfo',
    WITHDRAWAL: 'withdrawal',
    ANNUAL_REPORT: 'annualReport',
    IDLE: 'idle',
    SEVERE_WEATHER: 'severeWeather',
};
const HOME_INFO_TAB_NAME = {
    USER: 'user',
    INSTALLATION: 'installation',
    CONTROL: 'control',
};
export default defineComponent({
    name: 'UserMainTemplateContainer',
    components: {
        MainTemplate,
        Header,
        Footer,
        PrivacyPopup,
        HomeInfoPopup,
        WithdrawalPopup,
        AnnualReportPopup,
        IdleTimePopup: defineAsyncComponent(() => import('@hems/component/src/popup/IdleTimePopup.vue')),
        SevereWeatherPopup,
    },
    emits: ['navigation', 'home'],
    async setup(props, { emit }) {
        const store = useStore();
        const userInfoService = new UserInfoService(window.axiosInstance.axios);
        const deviceService = new DeviceServiceUser(window.axiosInstance.axios);
        const commonService = new CommonService(window.axiosInstance.axios);
        const notificationService = new NotificationService(window.axiosInstance.axios);
        const homeApplianceService = new HomeApplianceService(window.axiosInstance.axios);
        const stormService = new StormCoverService(window.axiosInstance.axios);
        const isInit = ref(false);
        const popupStatus = ref({
            privacy: { on: false },
            homeInfo: { on: false },
            withdrawal: { on: false },
            annualReport: { on: false },
            idle: { on: false },
            severeWeather: { on: false },
        });
        const { isUS } = useLocale();
        const languageCode = computed(() => store.state.appCtx.langCd);
        const isExistUnrecognitionNotification = ref(false);
        const notificationList = ref([]);
        const hasHomeAppliance = ref(false);
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const homeInfoTabName = ref('user');
        const isStormCoverSupportedDevice = computed(() => (store.state.device.siteConfiguration?.hasHub && store.state.device.gen_type === GEN_TYPE.AC_SYS) ?? false);
        const siteInstallationDate = computed(() => store.state.device.installation?.install_dt ?? '');
        const onClickNavigation = (e, path) => {
            emit('navigation', e, path);
        };
        const goHome = () => {
            emit('home');
        };
        const openPopup = (popupName) => {
            popupStatus.value[popupName] = { ...popupStatus.value[popupName], on: true };
        };
        const closePopup = (popupName) => {
            popupStatus.value[popupName] = { ...popupStatus.value[popupName], on: false };
        };
        const onHomeInfo = () => {
            openPopup(POPUP_NAME.HOME_INFO);
        };
        const onWithdrawal = () => {
            closePopup(POPUP_NAME.HOME_INFO);
            openPopup(POPUP_NAME.WITHDRAWAL);
        };
        const annualReportPopup = () => {
            openPopup(POPUP_NAME.ANNUAL_REPORT);
        };
        const onLogout = () => {
            if (popupStatus.value.idle.on == true) {
                closePopup(POPUP_NAME.IDLE);
            }
            AuthHelper.removeAccessToken();
            window.axiosInstance.clearAccessToken();
            location.href = '/';
        };
        const { idle } = useIdle(ONE_HOUR_VALUE_IN_MILLISECONDS);
        watch(idle, (idleValue) => {
            if (idleValue) {
                openPopup(POPUP_NAME.IDLE);
            }
        });
        const onContinue = () => {
            closePopup(POPUP_NAME.IDLE);
        };
        const openPrivacyPopup = () => {
            openPopup(POPUP_NAME.PRIVACY);
        };
        const { alertMessageBox } = useMessageBox();
        const { accessToken } = useAccessToken();
        if (!accessToken) {
            alertMessageBox('Please Log in');
            window.location.href = '/';
        }
        const jwtObject = AuthHelper.parseJwt(accessToken);
        if (!jwtObject) {
            alertMessageBox('Invalid token');
            window.location.href = '/';
        }
        const username = jwtObject?.preferred_username;
        const roleCd = jwtObject?.auth_type_cd;
        const roleNm = jwtObject?.auth_type_cd ? AuthHelper.getRoleNm(jwtObject.auth_type_cd) : null;
        store
            .dispatch('user/setRole', {
            roleCd,
            roleNm,
        })
            .then(async () => {
            store.dispatch('appCtx/setGoogleApiKey', await commonService.getGoogleAPIKey());
        });
        store.dispatch('user/setUserId', username);
        window.axiosInstance.setAccessToken(accessToken);
        const getUserInfo = async () => {
            const result = await userInfoService.getGeneralAccountInfo();
            store.dispatch('appCtx/setTermsVer', result.terms_ver.split('-')[0]);
        };
        const getSiteId = async () => {
            try {
                const siteId = await deviceService.getSiteId();
                store.dispatch('user/setHomeId', { homeId: siteId });
            }
            catch (e) {
                console.error(e);
            }
        };
        const getUserInstallationInfo = async () => {
            const { status, result } = await deviceService.getUserInstallationInfo();
            if (status === 'ok' && result !== null) {
                let installationInfo;
                if (Array.isArray(result)) {
                    const primaryInstallationInfo = result.find((item) => item.ems_type_cd === EMS_TYPE.PRIMARY);
                    if (primaryInstallationInfo) {
                        installationInfo = primaryInstallationInfo;
                    }
                    else {
                        const standAloneInstallationInfo = result.find((item) => item.ems_type_cd === EMS_TYPE.STAND_ALONE);
                        installationInfo = standAloneInstallationInfo ?? result[0];
                    }
                    store.dispatch(DEVICE_ACTION.SET_DEVICE_LIST, result);
                }
                else {
                    installationInfo = result;
                    store.dispatch(DEVICE_ACTION.SET_DEVICE_LIST, [result]);
                }
                store.dispatch('user/setOwnDeviceId', { deviceId: installationInfo.device_id });
                store.dispatch('appCtx/setTimeZone', installationInfo.timezone);
                store.dispatch(DEVICE_ACTION.SET_BASE_DEVICE_INFO, {
                    device_id: installationInfo.device_id,
                    site_id: store.state.user.homeId,
                    timezone_id: installationInfo.timezone,
                    product_model_nm: installationInfo.product_model_nm,
                });
                const genType = await commonService.getGenType(Number(store.state.user.homeId));
                store.dispatch(DEVICE_ACTION.SET_GEN_TYPE, genType);
                store.dispatch(DEVICE_ACTION.SET_INSTALLATION_INFO, installationInfo);
            }
        };
        const getSiteConfigurationInfo = async () => {
            const defaultConfigurationInfo = {
                hasAcSystem: false,
                hasESS: true,
                hasHub: true,
                hasPV: true,
                hasThirdParty: false,
            };
            try {
                const siteId = store.state.user.homeId;
                const { stepData } = await deviceService.getInstallationSteps(Number(siteId));
                if (stepData) {
                    const siteConfigurationInfo = stepData.systemConfiguration ?? defaultConfigurationInfo;
                    store.dispatch(DEVICE_ACTION.SET_SITE_CONFIGURATION_INFO, siteConfigurationInfo);
                }
                else {
                    store.dispatch(DEVICE_ACTION.SET_SITE_CONFIGURATION_INFO, defaultConfigurationInfo);
                }
            }
            catch (e) {
                console.error(e);
                store.dispatch(DEVICE_ACTION.SET_SITE_CONFIGURATION_INFO, defaultConfigurationInfo);
            }
        };
        const checkIsConsumptionCT = async () => {
            try {
                const siteId = store.state.user.homeId;
                const settingInfo = await deviceService.getSettingInfo(Number(siteId));
                const isConsumptionCT = settingInfo.accb_consumption_ct_installed_point !== CONSUMPTION_CT_TYPE.NOT_INSTALLED;
                store.dispatch(DEVICE_ACTION.SET_IS_CONSUMPTION_CT, isConsumptionCT);
            }
            catch (e) {
                console.error(e);
                store.dispatch(DEVICE_ACTION.SET_IS_CONSUMPTION_CT, true);
            }
        };
        const getStormInfo = async () => {
            try {
                const siteId = store.state.user.homeId;
                const { stormCoverEvent, stormCoverStatus, stormCoverActivated } = await stormService.getStormInfo(Number(siteId));
                if (stormCoverEvent) {
                    store.dispatch(STORM_ACTION.SET_STORM_INFO, { stormCoverEvent, stormCoverStatus, stormCoverActivated });
                    popupStatus.value.severeWeather.on = true;
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const getACSystemType = () => {
            const deviceList = store.state.device.deviceList?.map((device) => {
                const ACModelType = Helper.getACModelType(device.product_model_nm);
                if (ACModelType === AC_MODEL_TYPE.AC_COMBINER) {
                    return { device_type_cd: AC_DEVICE_TYPE.AC_COMBINER };
                }
                return { device_type_cd: AC_DEVICE_TYPE.AC_ESS };
            }) ?? [];
            const siteConfiguration = store.state.device.siteConfiguration;
            const ACDeviceType = siteConfiguration ? DeviceHelper.getACSystemType(deviceList, siteConfiguration) : '';
            store.dispatch(DEVICE_ACTION.SET_AC_DEVICE_TYPE, ACDeviceType);
        };
        const setSiteStoreInfo = async () => {
            const siteId = store.state.user.homeId;
            const deviceService = new DeviceServiceUser(window.axiosInstance.axios);
            try {
                const deviceProfile = await deviceService.getDeviceProfile(Number(siteId));
                store.dispatch(SITE_ACTION.SET_DEVICE_LIST, deviceProfile?.device_list.map((device) => ({
                    genType: device.gen_type,
                    deviceInfo: device.device_info,
                })));
                store.dispatch(SITE_ACTION.SET_HUB_INFO, deviceProfile?.hub_info);
            }
            catch (e) {
                console.error(e);
            }
        };
        const getIsUnRecognitionNotification = async () => {
            try {
                if (!isUS) {
                    const { isUnRecognition } = await notificationService.getUnRecognitionNotification();
                    isExistUnrecognitionNotification.value = isUnRecognition;
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const getNotificationList = async () => {
            try {
                if (!isUS) {
                    const result = await notificationService.getNotificationList({ pageNumber: 1, listSize: 10 });
                    notificationList.value = result;
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const onOpenNotificationList = async () => {
            try {
                if (!isUS && isExistUnrecognitionNotification.value) {
                    await notificationService.updateNotificationRecognition({ notificationIds: 'ALL' });
                    isExistUnrecognitionNotification.value = false;
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const updateNotificationRead = async (notificationId, isRead = false) => {
            try {
                if (!isUS && !isRead) {
                    await notificationService.updateNotificationRead(notificationId);
                    await getNotificationList();
                }
            }
            catch (e) {
                console.error(e);
            }
        };
        const checkHasHomeAppliance = async () => {
            const siteId = store.state.user.homeId;
            try {
                const homeApplianceList = await homeApplianceService.getApplianceList(Number(siteId));
                const filteredHomeApplianceList = homeApplianceList.filter((item) => item.profile_cd === Constant.APPLIANCE_TYPE.HEAT_PUMP ||
                    item.profile_cd === Constant.APPLIANCE_TYPE.AIR_CONDITIONER ||
                    item.profile_cd === Constant.APPLIANCE_TYPE.SMART_PLUG ||
                    item.profile_cd === Constant.APPLIANCE_TYPE.TV);
                return filteredHomeApplianceList.length > 0;
            }
            catch (e) {
                console.error(e);
                return false;
            }
        };
        const openStormCoverSetting = () => {
            popupStatus.value.homeInfo = { on: true };
            homeInfoTabName.value = HOME_INFO_TAB_NAME.CONTROL;
        };
        // 하루 4번호출(6시간 간격)
        const sixHourInterval = 6 * 60 * 60 * 1000;
        const stormInterval = setInterval(() => {
            if (isStormCoverSupportedDevice.value) {
                getStormInfo();
            }
        }, sixHourInterval);
        provide(PROVIDE_INJECT_KEYS.CONTROL_POPUP, { onHomeInfo });
        onBeforeUnmount(() => {
            if (isStormCoverSupportedDevice.value) {
                clearInterval(stormInterval);
            }
        });
        await getSiteId();
        await Promise.all([
            getUserInfo(),
            getUserInstallationInfo(),
            getSiteConfigurationInfo(),
            getIsUnRecognitionNotification(),
            getNotificationList(),
            checkHasHomeAppliance(),
        ]);
        if (isUS) {
            getACSystemType();
            const ACDeviceType = store.state.device.acDeviceType;
            if (ACDeviceType === AC_DEVICE_TYPE.AC_COMBINER) {
                await checkIsConsumptionCT();
            }
            await setSiteStoreInfo();
        }
        if (isStormCoverSupportedDevice.value) {
            await getStormInfo();
        }
        isInit.value = true;
        return {
            isInit,
            popupStatus,
            POPUP_NAME,
            isUS,
            isStormCoverSupportedDevice,
            languageCode,
            isExistUnrecognitionNotification,
            notificationList,
            hasHomeAppliance,
            homeInfoTabName,
            goHome,
            onClickNavigation,
            openPopup,
            closePopup,
            onLogout,
            onHomeInfo,
            onWithdrawal,
            annualReportPopup,
            onOpenNotificationList,
            updateNotificationRead,
            onContinue,
            openStormCoverSetting,
            openPrivacyPopup,
            baseDeviceInfo,
            siteInstallationDate,
        };
    },
    errorCaptured(error, instance, info) {
        console.error('MainTemplate ErrorCaptured', error, instance, info);
    },
});
